import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Section, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2, Plus, Pencil } from "lucide-react";
import ExportImport from "@/components/ExportImport";
import HistoryButton from "@/components/HistoryButton";
import PhotoUpload from "@/components/PhotoUpload";

/**
 * Generic CRUD list with dialog form + edit-in-place.
 * columns: [{key, label, render?}]
 * fields: [{key, label, type: text|number|date|select, options?, required?, placeholder?}]
 */
export default function CrudList({ title, subtitle, endpoint, columns, fields, canDelete = true, canEdit = true, extraActions, transformSubmit, initialForm, exportModule, importable = true, auditEntity }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialForm || {});

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startAdd = () => {
    setEditId(null);
    setForm(initialForm || {});
    setOpen(true);
  };

  const startEdit = (row) => {
    // strip system fields
    const clean = { ...row };
    ["id", "created_at", "created_by", "updated_at", "updated_by"].forEach((k) => delete clean[k]);
    setForm(clean);
    setEditId(row.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = transformSubmit ? transformSubmit(form) : { ...form };
      for (const f of fields) {
        if (f.type === "number" && payload[f.key] !== undefined && payload[f.key] !== "") {
          payload[f.key] = Number(payload[f.key]);
        }
      }
      if (editId) {
        await api.put(`${endpoint}/${editId}`, payload);
        toast.success("Data diperbarui");
      } else {
        await api.post(endpoint, payload);
        toast.success("Data tersimpan");
      }
      setOpen(false);
      setEditId(null);
      setForm(initialForm || {});
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus data ini?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Terhapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const testIdBase = endpoint.replace(/\//g, '-').replace(/^-/, '');
  const auditKind = auditEntity || testIdBase.replace(/-/g, "_");
  const showActions = canEdit || canDelete || !!auditEntity || !!auditKind;

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        testId={`${testIdBase}-header`}
        actions={
          <>
            {exportModule && <ExportImport module={exportModule} canImport={importable} />}
            {extraActions}
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setForm(initialForm || {}); } }}>
              <DialogTrigger asChild>
                <Button onClick={startAdd} data-testid={`${testIdBase}-add-btn`} className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold">
                  <Plus size={16} className="mr-1" /> Tambah
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-display font-black text-2xl">{editId ? "Edit" : "Tambah"} {title}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  {fields.map((f) => (
                    <div key={f.key} className={f.wide ? "md:col-span-2" : ""}>
                      <Label className="text-xs font-mono uppercase text-neutral-400 tracking-wider">{f.label}</Label>
                      {f.type === "photo" ? (
                        <PhotoUpload
                          value={form[f.key]}
                          onChange={(u) => setForm({ ...form, [f.key]: u })}
                          kind={f.kind || "attachment"}
                          label=""
                        />
                      ) : f.type === "select" ? (
                        <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                          <SelectTrigger data-testid={`${testIdBase}-field-${f.key}`} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11">
                            <SelectValue placeholder={f.placeholder || "Pilih..."} />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                            {(f.options || []).map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          data-testid={`${testIdBase}-field-${f.key}`}
                          type={f.type || "text"}
                          value={form[f.key] ?? ""}
                          onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                          placeholder={f.placeholder}
                          required={f.required}
                          className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11"
                        />
                      )}
                    </div>
                  ))}
                  <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-neutral-800">Batal</Button>
                    <Button type="submit" data-testid={`${testIdBase}-submit-btn`} className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold">
                      {editId ? "Perbarui" : "Simpan"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </>
        }
      />
      <Section>
        {loading ? (
          <div className="text-sm text-neutral-500 font-mono">Memuat...</div>
        ) : items.length === 0 ? (
          <Empty />
        ) : (
          <div className="border border-neutral-800 bg-[#141414]">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  {columns.map((c) => (
                    <TableHead key={c.key} className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{c.label}</TableHead>
                  ))}
                  {showActions && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id} className="border-neutral-800 hover:bg-neutral-900" data-testid={`${testIdBase}-row-${row.id}`}>
                    {columns.map((c) => (
                      <TableCell key={c.key} className="font-mono text-sm">
                        {c.render ? c.render(row) : (row[c.key] ?? "-")}
                      </TableCell>
                    ))}
                    {showActions && (
                      <TableCell className="text-right space-x-3">
                        <HistoryButton entityType={auditKind} entityId={row.id} testId={`${testIdBase}-history-${row.id}`} />
                        {canEdit && (
                          <button onClick={() => startEdit(row)} data-testid={`${testIdBase}-edit-${row.id}`} className="text-neutral-500 hover:text-[#FF6600]">
                            <Pencil size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => remove(row.id)} data-testid={`${testIdBase}-delete-${row.id}`} className="text-neutral-500 hover:text-[#DC2626]">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
    </div>
  );
}
